import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AdminUserStatus = "active" | "inactive" | "waitlisted" | "invited" | "pending_delete";

export interface AdminUser {
  id: string;
  email: string | null;
  name: string | null;
  role: string | null;
  status: AdminUserStatus;
  provider_type?: string | null;
  avatar_url?: string | null;
  last_login?: string | null;
  last_auth_method?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  source: "profile" | "waitlist";
}

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ["admin-users"],
    queryFn: async () => {
      // Try RPC first (bypasses RLS via SECURITY DEFINER)
      const { data: rpcData, error: rpcError } = await supabase.rpc("admin_list_users" as any);
      if (!rpcError && Array.isArray(rpcData)) {
        return (rpcData || []).map((r: any) => ({
          id: String(r.id),
          email: r.email ?? null,
          name: r.name ?? null,
          role: r.role ?? null,
          status: (r.status || "inactive") as AdminUserStatus,
          provider_type: r.provider_type ?? null,
          avatar_url: r.avatar_url ?? null,
          last_login: r.last_login ?? null,
          last_auth_method: r.last_auth_method ?? null,
          created_at: r.created_at ?? null,
          updated_at: r.updated_at ?? null,
          source: (r.source || "profile") as "profile" | "waitlist",
        })) as AdminUser[];
      }

      // Fallback: client-side merge (requires permissive RLS)
      const [profilesRes, waitlistRes] = await Promise.all([
        supabase.from("user_profiles").select("*"),
        supabase.from("user_waitlist").select("*")
      ]);

      const profiles = (profilesRes?.data || []).map((p: any) => {
        const name = [p.first_name, p.last_name].filter(Boolean).join(" ") || null;
        const status: AdminUserStatus = p.is_active ? "active" : "inactive";
        return {
          id: String(p.id),
          email: p.email ?? null,
          name,
          role: p.role ?? null,
          status,
          provider_type: p.provider_type ?? null,
          avatar_url: p.avatar_url ?? null,
          last_login: p.last_login ?? p.last_auth_timestamp ?? null,
          last_auth_method: p.last_auth_method ?? null,
          created_at: p.created_at ?? null,
          updated_at: p.updated_at ?? null,
          source: "profile" as const,
        } as AdminUser;
      });

      const waitlisted = (waitlistRes?.data || []).map((w: any) => {
        const computedFallbackName = [w.first_name, w.last_name].filter(Boolean).join(" ") || null;
        const name = w.name ?? computedFallbackName;
        return {
          id: String(w.id),
          email: w.email ?? null,
          name,
          role: null,
          status: (w.invited_at ? "invited" : "waitlisted") as AdminUserStatus,
          provider_type: w.provider_type ?? null,
          avatar_url: w.avatar_url ?? null,
          last_login: null,
          last_auth_method: null,
          created_at: w.created_at ?? null,
          updated_at: w.updated_at ?? null,
          source: "waitlist" as const,
        } as AdminUser;
      });

      return [...profiles, ...waitlisted].sort((a, b) => {
        const at = new Date((a.updated_at || a.created_at || '') as string).getTime() || 0;
        const bt = new Date((b.updated_at || b.created_at || '') as string).getTime() || 0;
        if (bt !== at) return bt - at;
        return String(b.id).localeCompare(String(a.id));
      });
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
